import React, { useState, useEffect, useCallback } from 'react'
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Card,
  Spinner,
  Alert,
  Badge,
} from 'react-bootstrap'
import { PnsMacMiniUserStatus } from 'interfaces/pns-mac-mini-user'
import { PnsMacMini } from 'interfaces/pns-mac-mini'
import useCreateMacMiniUsers from 'hooks/use-create-mac-mini-users'

interface MacMiniUserFormData {
  username: string
  status: PnsMacMiniUserStatus
  wispPort: number
  isEditing: boolean
}

interface MacMiniUserCreationModalProps {
  show: boolean
  onHide: () => void
  macMini: PnsMacMini
  onSuccess: () => void
  onCancel?: () => void
}

const MacMiniUserCreationModal: React.FC<MacMiniUserCreationModalProps> = ({
  show,
  onHide,
  macMini,
  onSuccess,
  onCancel,
}) => {
  const [baseUsername, setBaseUsername] = useState('')
  const [users, setUsers] = useState<MacMiniUserFormData[]>([])
  const [errors, setErrors] = useState<string[]>([])

  // Initialize 5 empty users from the start
  const initializeEmptyUsers = (): MacMiniUserFormData[] => {
    return Array.from({ length: 5 }, (_, index) => ({
      username: '', // Start with empty usernames
      status: PnsMacMiniUserStatus.AVAILABLE,
      wispPort: 8001 + index,
      isEditing: false,
    }))
  }

  const createUsersMutation = useCreateMacMiniUsers()

  // Generate 5 users based on base username
  const generateUsers = (base: string): MacMiniUserFormData[] => {
    if (!base.trim()) return []

    // Extract the number from base username (e.g., A7M1P1 -> 1)
    const match = base.match(/P(\d+)$/)
    const baseNumber = match ? parseInt(match[1], 10) : 1
    const prefix = match
      ? base.substring(0, base.lastIndexOf('P') + 1)
      : `${base}P`

    return Array.from({ length: 5 }, (_, index) => ({
      username: `${prefix}${baseNumber + index}`,
      status: PnsMacMiniUserStatus.AVAILABLE,
      wispPort: 8001 + index, // Keep port logic separate from naming
      isEditing: false,
    }))
  }

  useEffect(() => {
    if (show) {
      setBaseUsername('')
      setUsers(initializeEmptyUsers()) // Always initialize with 5 empty users
      setErrors([])
    }
  }, [show])

  // Make sure initializeEmptyUsers is stable
  const initializeEmptyUsersStable = useCallback(() => {
    return Array.from({ length: 5 }, (_, index) => ({
      username: '', // Start with empty usernames
      status: PnsMacMiniUserStatus.AVAILABLE,
      wispPort: 8001 + index,
      isEditing: false,
    }))
  }, [])

  const handleGenerate = () => {
    if (!baseUsername.trim()) {
      setErrors(['Please enter a base Mac Mini user code'])
      return
    }

    const generatedUsers = generateUsers(baseUsername)
    // Update existing users with generated names, keep their current editing states
    setUsers((prevUsers) =>
      prevUsers.map((user, index) => ({
        ...user,
        username: generatedUsers[index]?.username || user.username,
      }))
    )
    setErrors([])
  }

  const handleUserChange = (
    index: number,
    field: keyof MacMiniUserFormData,
    value: any
  ) => {
    setUsers((prev) => {
      const updated = [...prev]
      updated[index] = { ...updated[index], [field]: value }
      return updated
    })
  }

  const toggleEdit = (index: number) => {
    setUsers((prev) => {
      const updated = [...prev]
      updated[index] = {
        ...updated[index],
        isEditing: !updated[index].isEditing,
      }
      return updated
    })
  }

  const validateUsers = (): string[] => {
    const validationErrors: string[] = []
    const usedPorts = new Set<number>()
    const usedUsernames = new Set<string>()

    users.forEach((user, index) => {
      const userNumber = index + 1

      // Username validation
      if (!user.username.trim()) {
        validationErrors.push(`User ${userNumber}: Username is required`)
      } else if (usedUsernames.has(user.username)) {
        validationErrors.push(`User ${userNumber}: Username must be unique`)
      } else if (user.username.length > 50) {
        validationErrors.push(
          `User ${userNumber}: Username too long (max 50 characters)`
        )
      }
      usedUsernames.add(user.username)

      // WISP Port validation
      if (user.wispPort < 1000 || user.wispPort > 65535) {
        validationErrors.push(
          `User ${userNumber}: WISP port must be between 1000-65535`
        )
      } else if (usedPorts.has(user.wispPort)) {
        validationErrors.push(`User ${userNumber}: WISP port must be unique`)
      }
      usedPorts.add(user.wispPort)
    })

    return validationErrors
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Check if any users have empty usernames
    const emptyUsers = users.filter((user) => !user.username.trim())
    if (emptyUsers.length > 0) {
      setErrors([
        'Please generate users or fill in all usernames before creating',
      ])
      return
    }

    const validationErrors = validateUsers()
    if (validationErrors.length > 0) {
      setErrors(validationErrors)
      return
    }

    setErrors([])

    try {
      await createUsersMutation.mutateAsync({
        macMiniId: macMini.id,
        users: users.map((user) => ({
          username: user.username,
          status: user.status,
          wispPort: user.wispPort,
        })),
      })

      onSuccess()
    } catch (error) {
      console.error('Failed to create Mac Mini users:', error)
    }
  }

  const handleCancel = () => {
    onCancel?.()
    onHide()
  }

  const getStatusVariant = (status: PnsMacMiniUserStatus) => {
    switch (status) {
      case PnsMacMiniUserStatus.ACTIVE:
        return 'success'
      case PnsMacMiniUserStatus.AVAILABLE:
        return 'info'
      case PnsMacMiniUserStatus.RESERVED:
        return 'warning'
      case PnsMacMiniUserStatus.ICLOUD_SETUP_IN_PROGRESS:
        return 'info'
      case PnsMacMiniUserStatus.INACTIVE:
        return 'secondary'
      case PnsMacMiniUserStatus.TERMINATED:
        return 'danger'
      default:
        return 'secondary'
    }
  }

  const statusOptions = Object.values(PnsMacMiniUserStatus)

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Create Mac Mini Users</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="mb-3">
          <Alert variant="info">
            <strong>Device:</strong> {macMini.name} <br />
            <strong>Status Transition:</strong> PROVISIONING → AVAILABLE <br />
            <strong>Action:</strong> Creating 5 Mac Mini users to make this
            device available for assignment
          </Alert>
        </div>

        {/* Base Username Input */}
        <Card className="mb-4">
          <Card.Header>
            <strong>Base Mac Mini User Code</strong>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={8}>
                <Form.Group>
                  <Form.Control
                    type="text"
                    value={baseUsername}
                    onChange={(e) => setBaseUsername(e.target.value)}
                    placeholder="A7M1P1"
                  />
                  <Form.Text className="text-muted">
                    Enter the first Mac Mini user code. We'll auto-generate the
                    remaining 4 users.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Button
                  variant="primary"
                  onClick={handleGenerate}
                  disabled={!baseUsername.trim()}
                  className="w-100"
                >
                  Generate Users
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {errors.length > 0 && (
          <Alert variant="danger">
            <Alert.Heading>Validation Errors</Alert.Heading>
            <ul className="mb-0">
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}

        {/* Mac Mini Users To Be Added - Always Visible */}
        <Card className="mb-3">
          <Card.Header>
            <strong>Mac Mini Users To Be Added</strong>
          </Card.Header>
          <Card.Body>
            {users.map((user, index) => (
              <Card key={index} className="mb-3">
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>User {index + 1}</strong>
                    <Badge
                      variant={getStatusVariant(user.status)}
                      className="ms-2"
                    >
                      {user.status.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                  <Button
                    variant={user.isEditing ? 'success' : 'outline-light'}
                    size="sm"
                    onClick={() => toggleEdit(index)}
                  >
                    {user.isEditing ? 'Save' : 'Edit'}
                  </Button>
                </Card.Header>

                {user.isEditing ? (
                  // Edit Mode - Show form fields
                  <Card.Body>
                    <Row>
                      <Col md={8}>
                        <Form.Group className="mb-2">
                          <Form.Label>Username</Form.Label>
                          <Form.Control
                            type="text"
                            value={user.username}
                            onChange={(e) =>
                              handleUserChange(
                                index,
                                'username',
                                e.target.value
                              )
                            }
                            placeholder={`A7M1P${1 + index}`}
                            maxLength={50}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-2">
                          <Form.Label>Status</Form.Label>
                          <Form.Control
                            as="select"
                            value={user.status}
                            onChange={(e) =>
                              handleUserChange(
                                index,
                                'status',
                                e.target.value as PnsMacMiniUserStatus
                              )
                            }
                          >
                            {statusOptions.map((status) => (
                              <option key={status} value={status}>
                                {status.replace(/_/g, ' ')}
                              </option>
                            ))}
                          </Form.Control>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                ) : (
                  // View Mode - Show current values or placeholders
                  <Card.Body>
                    <Row>
                      <Col md={8}>
                        <strong>Username:</strong>{' '}
                        {user.username || (
                          <span className="text-muted">Not set</span>
                        )}
                      </Col>
                      <Col md={4}>
                        <strong>Status:</strong>{' '}
                        {user.status.replace(/_/g, ' ')}
                      </Col>
                    </Row>
                  </Card.Body>
                )}
              </Card>
            ))}
          </Card.Body>
        </Card>

        <Alert variant="warning">
          <strong>Important:</strong> These users will be created on the Mac
          Mini and the device status will be updated to AVAILABLE. This action
          cannot be easily undone.
        </Alert>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={handleCancel}
          disabled={createUsersMutation.isLoading}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={createUsersMutation.isLoading}
        >
          {createUsersMutation.isLoading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Creating Users...
            </>
          ) : (
            'Create Users & Update Status'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default MacMiniUserCreationModal
